import React from 'react';
import { TouchableWithoutFeedback, View } from 'react-native-web';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Janitor from '../../modules/Janitor';
import ModalContext from './ModalContext';
import './ModalHost.module.scss';
import modalRegistry from '../../modules/modalRegistry';
/**
 * @deprecated
 * Container for showing Modals.
 *
 * See also:
 *   Modal
 *   ModalTrigger
 */
export default class ModalHost extends React.Component {
  currentIndex = 0;
  unmountedIdsRemoved = null;
  janitor = new Janitor();

  static propTypes = {
    children: PropTypes.node,
    dismissWhenOverlayClicked: PropTypes.bool,
    /** A modal that is always present at the base of this modal stack */
    staticModal: PropTypes.node,
    isRegistered: PropTypes.bool,
  };

  static defaultProps = {
    children: null,
    dismissWhenOverlayClicked: true,
    staticModal: null,
    isRegistered: false,
  };

  state = {
    instances: [],
  };

  componentWillUnmount() {
    this.unmountedIdsRemoved = [];
    this.janitor.cleanup();
  }

  handleAdd = (modal, onRemove) => {
    if (this.unmountedIdsRemoved !== null) {
      console.warn('ModalHost add called after unmount');
      return null;
    }

    this.currentIndex += 1;

    const id = this.currentIndex;
    const instance = {
      id,
      modal,
      onRemove,
      removing: false,
    };
    this.setState(({ instances }) => ({
      instances: [...instances, instance],
    }));
    if (!this.props.isRegistered) {
      // If the modal isn't registered beforehand, then it's likely a direct use of ModalHost
      // which means it's not being rendered in the portal element, so update z indexes
      modalRegistry.registerModal({
        id: `modalHost${id}`,
        inPortalElementRoot: false,
      });
    }

    return id;
  };

  handleRemove = (id, result) => {
    const remove = this.state.instances.find(instance => instance.id === id);
    if (!this.props.isRegistered) {
      modalRegistry.unregisterModal(`modalHost${id}`);
    }
    if (!remove) {
      return;
    }

    // Only call onRemove once, even after unmount
    if (this.unmountedIdsRemoved !== null) {
      if (this.unmountedIdsRemoved.includes(id)) {
        return;
      }
      this.unmountedIdsRemoved.push(id);
    }

    // Notify in case host is closing the modal and not the trigger, e.g. overlay clicked
    if (remove.onRemove) {
      remove.onRemove(result);
    }

    // Don't update state if unmounted
    if (this.unmountedIdsRemoved === null) {
      this.setState(({ instances }) => ({
        instances: instances.map(instance => {
          if (instance.id === id) {
            return {
              ...instance,
              // trigger the exit animation for this modal
              removing: true,
            };
          }

          return instance;
        }),
      }), () => {
        this.janitor.addTimeout(setTimeout(() => {
          // completely remove the modal after it has animated away
          this.setState(({ instances }) => ({
            instances: instances.filter(instance => instance.id !== id),
          }));
        }, 250)); // 250 is slightly longer than the animation speed
      });
    }
  };

  handleOverlayClick = event => {
    event.preventDefault();

    const { instances } = this.state;
    const { dismissWhenOverlayClicked } = this.props;

    if (instances.length > 0 && dismissWhenOverlayClicked) {
      this.handleRemove(instances[instances.length - 1].id);
    }
  };

  // eslint-disable-next-line class-methods-use-this
  renderModalContainer(instance, isTop) {
    const pointerEvents = isTop ? 'box-none' : 'none';
    const {
      modal,
      id,
      removing,
    } = instance;

    return (
      <View key={id} styleName={classNames('modal-container', { closing: removing })} pointerEvents={pointerEvents}>
        {modal}
        {!isTop && (
          <View styleName={classNames('modal-overlay', { closing: removing })} />
        )}
      </View>
    );
  }

  render() {
    const { children, staticModal } = this.props;
    const { instances } = this.state;
    const allInstances = [...(staticModal ? [{
      modal: staticModal,
      id: 0,
      removing: false,
    }] : []), ...instances];

    const context = {
      add: this.handleAdd,
      remove: this.handleRemove,
    };

    return (
      <ModalContext.Provider value={context}>
        <View styleName="modal-host">
          {children}
          {allInstances.length > 0 && (
            <TouchableWithoutFeedback key="overlay" onPress={this.handleOverlayClick}>
              <View styleName={classNames('modal-overlay', { closing: allInstances.length === 1 && allInstances[0].removing })} />
            </TouchableWithoutFeedback>
          )}
          {allInstances.map((instance, index) => (
            this.renderModalContainer(instance, index === allInstances.length - 1)
          ))}
        </View>
      </ModalContext.Provider>
    );
  }
}
